import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  append,
  find,
  mergeDeepRight,
  mergeRight,
  propEq,
  remove,
} from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { useAllModules } from "../../manage-subscriptions/subscriptionService";
import DefaultModulesDialog from "../edit/DefaultModulesDialog";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const DefaultModulesCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [modules, setModules] = useState([]);
  const modulesResponse = useAllModules();
  useEffect(() => {
    if (modulesResponse.isSuccess && notBlank(modulesResponse.data)) {
      setModules(modulesResponse.data);
    }
  }, [modulesResponse.isSuccess, modulesResponse.data]);

  const findModuleName = useCallback(
    moduleId => {
      const module = find(propEq("id", moduleId), modules);
      return notBlank(module) ? module.displayName : null;
    },
    [modules],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      setDialogOpen(false);
      console.log(project);
      const update = mergeRight(project, {
        modules: append(data.id, project.modules),
      });
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        modules: remove(idx, 1, project.modules),
      });
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title="Default Modules"
        action={
          <Tooltip title="Add Module">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Project Details.
          </Box>
        )}
        {isBlank(project.modules) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Default Modules set up for this Project.
          </Box>
        )}
        {notBlank(project.modules) && (
          <List dense>
            {mapIndexed(
              (module, idx) => (
                <ListItem key={module}>
                  <Tooltip title={module}>
                    <ListItemText
                      primaryTypographyProps={{
                        noWrap: true,
                        style: { fontSize: "1.2rem" },
                      }}
                      primary={findModuleName(module)}
                    />
                  </Tooltip>
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() => handleDeleteClick(idx)}
                      dialogTitle="Remove Default Module"
                      dialogBody="Are you sure you want to remove this default module?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              project.modules,
            )}
          </List>
        )}
      </CardContent>
      {dialogOpen && (
        <DefaultModulesDialog
          project={project}
          open={dialogOpen}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default DefaultModulesCard;
